import { useEffect, useState } from 'react';
import { Loader2, PencilLine, Send, Copy, ShieldCheck, Handshake, X } from 'lucide-react';
import api from '../../api';
import { useToast } from '../../hooks/use-toast';

const STATUS = {
  to_prepare: { l: 'À préparer', c: 'bg-gray-200 text-gray-700' },
  sent: { l: 'Envoyé', c: 'bg-blue-100 text-blue-700 border border-blue-300' },
  waiting_acceptance: { l: 'En attente', c: 'bg-amber-100 text-amber-800 border border-amber-400' },
  accepted: { l: 'Signé', c: 'bg-green-100 text-green-700 border border-green-400' },
  refused: { l: 'Refusé', c: 'bg-red-100 text-red-700 border border-red-400' },
};

/** Inline manufacturer agreement panel — opens a mini-dialog when the admin clicks
 *  "Accord" on a manufacturer row inside MandateManager. */
export default function ManufacturerAgreementDialog({ manufacturerId, manufacturerName, open, onClose, onChange }) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [a, setA] = useState(null);
  const [lang, setLang] = useState('en');
  const [email, setEmail] = useState('');
  const [contact, setContact] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/manufacturers/${manufacturerId}/agreement`);
      setA(res.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open, manufacturerId]);

  const prepare = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/manufacturers/${manufacturerId}/agreement/prepare`, { lang });
      toast({ title: 'Accord préparé', description: `Version ${lang.toUpperCase()} snapshottée.`, duration: 6000 });
      load();
    } catch (e) { toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Impossible', duration: 10000 }); }
    finally { setBusy(false); }
  };
  const send = async () => {
    if (!email.trim()) { toast({ title: 'Courriel requis', description: 'Saisissez le courriel du manufacturier.', duration: 6000 }); return; }
    setBusy(true);
    try {
      const res = await api.post(`/admin/manufacturers/${manufacturerId}/agreement/send`, { email, contactName: contact });
      toast({ title: 'Accord envoyé', description: res.data?.emailSent ? `Lien envoyé à ${email}` : 'Lien généré (courriel non envoyé)', duration: 10000 });
      load(); if (onChange) onChange();
    } catch (e) { toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Impossible', duration: 10000 }); }
    finally { setBusy(false); }
  };
  const copyLink = async () => {
    if (!email.trim()) { toast({ title: 'Courriel requis', description: 'Saisissez le courriel du manufacturier.', duration: 6000 }); return; }
    try {
      const res = await api.post(`/admin/manufacturers/${manufacturerId}/agreement/send`, { email, contactName: contact });
      await navigator.clipboard.writeText(res.data.url);
      toast({ title: 'Lien copié', description: 'Collez-le dans un courriel externe si besoin.', duration: 6000 });
      load(); if (onChange) onChange();
    } catch (e) { toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Impossible', duration: 10000 }); }
  };

  if (!open) return null;

  const exists = a?.exists;
  const status = a?.status || 'to_prepare';
  const info = STATUS[status] || STATUS.to_prepare;
  const isAccepted = status === 'accepted';

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="ma-dialog">
      <div className="bg-white rounded-2xl max-w-lg w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="bg-brand-navy text-white px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-brand-yellow text-[10px] uppercase tracking-widest font-bold">Accord de collaboration</div>
            <h2 className="font-bold">{manufacturerName || 'Manufacturier'}</h2>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          {loading ? (
            <div className="flex items-center gap-2 text-gray-500 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Chargement…</div>
          ) : (
            <>
              <div className="flex items-center justify-between gap-2">
                <div className="text-sm text-gray-600">Statut :</div>
                <span className={`inline-block text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider ${info.c}`}>{info.l}</span>
              </div>
              {isAccepted && a?.acceptedAt && (
                <div className="p-2 rounded bg-green-50 border border-green-300 text-xs text-brand-navy flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-green-600 shrink-0" />
                  <span>Signé le {new Date(a.acceptedAt).toLocaleString('fr-CA')} par <b>{a.signerName}</b> ({a.signerCompany}) · méthode <b>{a.method}</b> · IP {a.signerIP || '—'}</span>
                </div>
              )}
              {!isAccepted && (
                <>
                  <div>
                    <label className="block text-[10px] font-bold text-brand-navy uppercase tracking-wider mb-1">Langue du modèle</label>
                    <div className="flex gap-1">
                      {['fr', 'en', 'zh'].map((l) => (
                        <button key={l} onClick={() => setLang(l)} data-testid={`ma-lang-${l}`} className={`flex-1 px-3 py-1.5 rounded text-xs font-bold uppercase transition ${lang === l ? 'bg-brand-yellow text-brand-navy' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
                          {l === 'fr' ? '🇫🇷 FR' : l === 'en' ? '🇬🇧 EN' : '🇨🇳 ZH'}
                        </button>
                      ))}
                    </div>
                  </div>
                  {!exists && (
                    <button data-testid="ma-prepare-btn" disabled={busy} onClick={prepare} className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-full border border-brand-navy text-brand-navy text-xs font-bold hover:bg-brand-navy hover:text-white disabled:opacity-50">
                      <PencilLine className="w-3.5 h-3.5" /> Préparer l'accord ({lang.toUpperCase()})
                    </button>
                  )}
                  {exists && (
                    <>
                      <div>
                        <label className="block text-[10px] font-bold text-brand-navy uppercase tracking-wider mb-1">Contact du manufacturier</label>
                        <input value={contact} onChange={(e) => setContact(e.target.value)} placeholder="Nom du contact" className="w-full px-3 py-2 border border-gray-200 rounded text-sm mb-2" />
                        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Courriel professionnel" className="w-full px-3 py-2 border border-gray-200 rounded text-sm" />
                      </div>
                      <div className="flex gap-2">
                        <button data-testid="ma-send-btn" disabled={busy || !email.trim()} onClick={send} className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-full bg-brand-yellow text-brand-navy text-xs font-bold hover:bg-yellow-400 disabled:opacity-50">
                          <Send className="w-3.5 h-3.5" /> Envoyer au manufacturier
                        </button>
                        <button onClick={copyLink} className="inline-flex items-center gap-1 px-3 py-2 rounded-full border border-gray-300 text-brand-navy text-xs font-semibold hover:border-brand-navy">
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <button onClick={prepare} className="w-full text-xs text-gray-500 hover:text-brand-navy underline">Régénérer avec la version courante du modèle</button>
                    </>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
